// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: small immutable helpers for editing a Monday Madness draft in the card.

import type {
	MondayDraftItem,
	MondayDraftSection,
	MondaySectionKind,
} from '@fluxer/schema/src/domains/konvo/MondayReportDraft';

export function newItem(extra: Partial<MondayDraftItem> = {}): MondayDraftItem {
	return {id: crypto.randomUUID(), text: '', url: null, minutes: null, note: '', state: null, ...extra};
}

export function newSection(kind: MondaySectionKind, title: string): MondayDraftSection {
	return {
		id: crypto.randomUUID(),
		title,
		body: '',
		kind,
		items: kind === 'text' ? [] : [newItem()],
		status: null,
		ref: null,
		hidden: false,
	};
}

export function moveEntry<T>(list: ReadonlyArray<T>, index: number, delta: number): Array<T> {
	const target = index + delta;
	if (target < 0 || target >= list.length) return [...list];
	const next = [...list];
	const [entry] = next.splice(index, 1);
	next.splice(target, 0, entry);
	return next;
}

export function replaceAt<T>(list: ReadonlyArray<T>, index: number, entry: T): Array<T> {
	return list.map((current, i) => (i === index ? entry : current));
}

export function removeAt<T>(list: ReadonlyArray<T>, index: number): Array<T> {
	return list.filter((_, i) => i !== index);
}

/** "1.5", "1,5", "1h 30m", "1:30", "90m" → minutes. Empty → null. Anything else → undefined. */
export function parseHours(text: string): number | null | undefined {
	const value = text.trim().toLowerCase().replace(',', '.');
	if (!value) return null;
	const clock = /^(\d+):(\d{1,2})$/.exec(value);
	if (clock) return Number(clock[1]) * 60 + Number(clock[2]);
	const parts = /^(?:(\d+(?:\.\d+)?)\s*h(?:ours?|rs?)?)?\s*(?:(\d+)\s*m(?:in(?:utes?)?)?)?$/.exec(value);
	if (parts && (parts[1] || parts[2])) return Math.round(Number(parts[1] ?? 0) * 60 + Number(parts[2] ?? 0));
	if (/^\d+(\.\d+)?$/.test(value)) return Math.round(Number(value) * 60);
	return undefined;
}

/** "Name <a@b.co>", "a@b.co" → recipient, or null when there is no usable address. */
export function parseRecipient(text: string): {name: string | null; address: string} | null {
	const value = text.trim().replace(/[,;]+$/, '');
	const angled = /^(.*?)<([^>]+)>$/.exec(value);
	const name = angled ? angled[1].trim().replace(/^"|"$/g, '') || null : null;
	const address = (angled ? angled[2] : value).trim();
	return /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(address) ? {name, address} : null;
}
